import { useState, useMemo, useEffect } from 'react'
import { OTCFilters } from '@/components/filters'
import { PaginationWrapper } from '@/components/organisms'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/utils'
import { InfoTooltip } from '@/components/ui/info-tooltip'
import type { OrderType, DealType, SortBy, SortDirection, OrderbookItem } from '@/types/otc'
import { useOTCFilters } from '@/hooks/useOTC'
import { OrderInquiryModal } from '@/components/organisms/modals'
import { OrderbookTableContent } from './OrderbookTableContent'
import { useIsMobile } from '@/hooks/use-mobile'
import { useAccessTier } from '@/hooks/useAccessTier'
import { useEnterPhaseTwo } from '@/components/gate'

interface OrderbookTableProps {
  title: string
  navigate: (path: string) => void
  useDataHook: (params: Record<string, any>) => any
  dataKey: string // 'orders' | 'latestOrders'
  showPagination?: boolean
  showSummary?: boolean
  useUrlParams?: boolean // Optional URL params support
  showViewSecondariesMarket?: boolean // Optional show View Secondaries Market button
  showOrderInquiry?: boolean // Optional show Order Inquiry modal instead of navigation
}

function getTrancheParams(trancheSize: string) {
  switch (trancheSize) {
    case '10k-100k':
      return { min_offered_amount: 10000, max_offered_amount: 100000 }
    case '100k-250k':
      return { min_offered_amount: 100000, max_offered_amount: 250000 }
    case '250k-500k':
      return { min_offered_amount: 250000, max_offered_amount: 500000 }
    case '1m+':
      return { min_offered_amount: 1000000 }
    default:
      return undefined
  }
}


const OrderbookTable = ({
  title,
  navigate,
  useDataHook,
  dataKey,
  showPagination,
  showSummary = true,
  useUrlParams = false,
  showViewSecondariesMarket = false,
  showOrderInquiry = false,
}: OrderbookTableProps) => {
  const isMobile = useIsMobile()
  const { canReadFinancials } = useAccessTier()
  const enterPhaseTwo = useEnterPhaseTwo()
  // URL-based filters (when useUrlParams = true)
  const urlFilters = useOTCFilters()
  const [orderInquiryModal, setOrderInquiryModal] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState<OrderbookItem | null>(null)
  const [activeTab, setActiveTab] = useState<'all' | 'buy' | 'sell'>('all')
  
  // Local state filters (when useUrlParams = false)
  const [localSearch, setLocalSearch] = useState('')
  const [localTrancheSize, setLocalTrancheSize] = useState('')
  const [localType, setLocalType] = useState<OrderType | ''>('')
  const [localAssetType, setLocalAssetType] = useState<DealType | ''>('')
  const [localSortBy, setLocalSortBy] = useState<SortBy | ''>('')
  const [localSortDirection, setLocalSortDirection] = useState<SortDirection | ''>('')

  // Handle tab changes
  const handleTabChange = (tab: 'all' | 'buy' | 'sell') => {
    setActiveTab(tab)
    if (useUrlParams) {
      if (tab === 'all') {
        urlFilters.setType('')
      } else {
        urlFilters.setType(tab.toUpperCase() as OrderType)
      }
    } else {
      if (tab === 'all') {
        setLocalType('')
      } else {
        setLocalType(tab.toUpperCase() as OrderType)
      }
    }
  }

  // Choose between URL params or local state
  const filters = useUrlParams ? urlFilters.filters : {
    search: localSearch,
    type: localType,
    assetType: localAssetType,
    trancheSize: localTrancheSize,
    sortBy: localSortBy,
    sortDirection: localSortDirection
  }

  // Sync activeTab with current filter
  useEffect(() => {
    if (filters.type === 'BUY') {
      setActiveTab('buy')
    } else if (filters.type === 'SELL') {
      setActiveTab('sell')
    } else {
      setActiveTab('all')
    }
  }, [filters.type])

  const setters = useUrlParams ? {
    setSearch: urlFilters.setSearch,
    setType: urlFilters.setType,
    setAssetType: urlFilters.setAssetType,
    setTrancheSize: urlFilters.setTrancheSize,
    setSortBy: urlFilters.setSortBy,
    setSortDirection: urlFilters.setSortDirection
  } : {
    setSearch: setLocalSearch,
    setType: setLocalType,
    setAssetType: setLocalAssetType,
    setTrancheSize: setLocalTrancheSize,
    setSortBy: setLocalSortBy,
    setSortDirection: setLocalSortDirection
  }

  const filterParams = useMemo(() => {
    const params: Record<string, any> = {}
    if (filters.search.trim()) params.search = filters.search.trim()
    if (filters.trancheSize) Object.assign(params, getTrancheParams(filters.trancheSize))
    if (filters.type) params.type = filters.type
    if (filters.assetType) params.deal_type = filters.assetType
    
    // Sort parameters are sent only together
    if (filters.sortBy) {
      params.sort_by = filters.sortBy
      // Sort Direction is applied only if Sort By is specified
      if (filters.sortDirection) {
        params.sort_direction = filters.sortDirection
      }
    }
    
    return Object.keys(params).length > 0 ? params : undefined
  }, [filters.search, filters.trancheSize, filters.type, filters.assetType, filters.sortBy, filters.sortDirection])

  // Memoize empty object for stability
  const emptyParams = useMemo(() => ({}), [])
  const stableParams = filterParams || emptyParams

  const dataHookResult = useDataHook(stableParams)

  /**
   * 🔴 Both destinations are financial.
   *
   * `OrderInquiryModal` prints the order's amount and FDV and exists to start a
   * trade; `/trading/otc/order/:id` is the order detail page, which is route-
   * gated anyway. A session without verification is routed into phase 2
   * instead, and brought back here when it finishes.
   */
  const handleOrderClick = (item: OrderbookItem) => {
    if (!canReadFinancials) {
      enterPhaseTwo()
      return
    }
    if (showOrderInquiry) {
      setSelectedOrder(item)
      setOrderInquiryModal(true)
    } else {
      navigate(`/trading/otc/order/${item.order_id}`)
    }
  }

  /** Placing an order is trading. Same rule. */
  const handlePlaceOrder = () => {
    if (!canReadFinancials) {
      enterPhaseTwo()
      return
    }
    navigate('/trading/otc/place-order')
  }

  const handleInquirySubmitted = () => {
    // Refresh data or show success message
    console.log('Order inquiry submitted successfully')
  }
  
  // Universal data retrieval depending on hook structure
  let data, loading, total, currentPage, totalPages, goToPage;
  
  if (dataHookResult.data && typeof dataHookResult.data === 'object' && dataKey in dataHookResult.data) {
    // For useMainScreen: data in data[dataKey]
    data = dataHookResult.data[dataKey] || []
    loading = dataHookResult.loading
    total = data.length
    currentPage = undefined
    totalPages = undefined
    goToPage = undefined
  } else if (dataKey in dataHookResult) {
    // When dataKey is orders, list lives on the hook result directly
    data = dataHookResult[dataKey] || []
    loading = dataHookResult.loading
    total = dataHookResult.total || data.length
    currentPage = dataHookResult.currentPage
    totalPages = dataHookResult.totalPages
    goToPage = dataHookResult.goToPage
  } else {
    // Fallback
    data = []
    loading = dataHookResult.loading || false
    total = 0
    currentPage = undefined
    totalPages = undefined
    goToPage = undefined
  }

  return (
    <div className={isMobile ? 'space-y-2' : 'space-y-9'}>
      {/* Header - Mobile: simple title + nav button, Desktop: with tabs */}
      {isMobile ? (
        <div className="space-y-2">
          <div className="flex h-[54px] items-center justify-between w-full">
            <p className="font-plus-jakarta font-bold leading-[24px] text-[#27292d] dark:text-[#fdfdfd] text-[20px] tracking-[-0.2px]">
              <span className="inline-flex items-center gap-2">
                {title}
                <InfoTooltip content="A live view of Secondaries orders. Filter and sort to find opportunities." ariaLabel={`${title} info`} />
              </span>
            </p>
            {showViewSecondariesMarket && (
              <button
                className="relative shrink-0 w-[27px] h-[27px] rounded-full bg-white dark:bg-[#1c1c1c] border border-[rgba(45,41,35,0.12)] dark:border-[rgba(253,253,253,0.12)] flex items-center justify-center transition-colors"
                onClick={() => (canReadFinancials ? navigate('/trading/otc') : enterPhaseTwo())}
                aria-label="View Secondaries Market"
              >
                <ArrowUpRight className="w-[14px] h-[14px] text-[#2d2923] dark:text-[#fdfdfd]" />
              </button>
            )}
          </div>

          <div className="flex w-full items-center gap-5">
            <button
              onClick={() => handleTabChange('all')}
              className={cn(
                'pb-2 pt-1 px-0 flex flex-1 items-center justify-center min-w-0 transition-colors',
                activeTab === 'all' && 'border-b border-[#2d2923] dark:border-[#fdfdfd] border-t-0 border-l-0 border-r-0 border-solid'
              )}
            >
              <p className={cn(
                'font-plus-jakarta leading-[20px] text-[14px] shrink-0',
                activeTab === 'all'
                  ? 'font-semibold text-[#2d2923] dark:text-[#fdfdfd]'
                  : 'font-medium text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]'
              )}>
                All
              </p>
            </button>
            <button
              onClick={() => handleTabChange('buy')}
              className={cn(
                'pb-2 pt-1 px-0 flex flex-1 items-center justify-center min-w-0 transition-colors',
                activeTab === 'buy' && 'border-b border-[#2d2923] dark:border-[#fdfdfd] border-t-0 border-l-0 border-r-0 border-solid'
              )}
            >
              <p className={cn(
                'font-plus-jakarta leading-[20px] text-[14px] shrink-0',
                activeTab === 'buy'
                  ? 'font-semibold text-[#2d2923] dark:text-[#fdfdfd]'
                  : 'font-medium text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]'
              )}>
                Buy
              </p>
            </button>
            <button
              onClick={() => handleTabChange('sell')}
              className={cn(
                'pb-2 pt-1 px-0 flex flex-1 items-center justify-center min-w-0 transition-colors',
                activeTab === 'sell' && 'border-b border-[#2d2923] dark:border-[#fdfdfd] border-t-0 border-l-0 border-r-0 border-solid'
              )}
            >
              <p className={cn(
                'font-plus-jakarta leading-[20px] text-[14px] shrink-0',
                activeTab === 'sell'
                  ? 'font-semibold text-[#2d2923] dark:text-[#fdfdfd]'
                  : 'font-medium text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]'
              )}>
                Sell
              </p>
            </button>
            <button
              onClick={handlePlaceOrder}
              className="pb-2 pt-1 px-0 flex items-center justify-center shrink-0 transition-colors"
            >
              <p className="font-plus-jakarta font-medium leading-[20px] text-[14px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] shrink-0 whitespace-nowrap">
                Place new order
              </p>
            </button>
          </div>
        </div>
      ) : (
        // Desktop Header - with tabs
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-[48px]">
            <p className="font-plus-jakarta font-semibold text-[23px] leading-[32px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd] shrink-0">
              <span className="inline-flex items-center gap-2">
                {title}
                <InfoTooltip content="A live view of Secondaries orders. Filter and sort to find opportunities." ariaLabel={`${title} info`} />
              </span>
            </p>
            <div className="flex items-center gap-5">
              <button
                onClick={() => handleTabChange('all')}
                className={cn(
                  "pb-2 pt-1 px-0 flex items-center justify-center shrink-0 transition-colors",
                  activeTab === 'all'
                    ? "border-b border-[#2d2923] dark:border-[#fdfdfd] border-t-0 border-l-0 border-r-0 border-solid"
                    : ""
                )}
              >
                <p className={cn(
                  "font-plus-jakarta leading-[20px] text-[14px] shrink-0",
                  activeTab === 'all'
                    ? "font-semibold text-[#2d2923] dark:text-[#fdfdfd]"
                    : "font-medium text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]"
                )}>
                  All
                </p>
              </button>
              <button
                onClick={() => handleTabChange('buy')}
                className={cn(
                  "pb-2 pt-1 px-0 flex items-center justify-center shrink-0 transition-colors",
                  activeTab === 'buy'
                    ? "border-b border-[#2d2923] dark:border-[#fdfdfd] border-t-0 border-l-0 border-r-0 border-solid"
                    : ""
                )}
              >
                <p className={cn(
                  "font-plus-jakarta leading-[20px] text-[14px] shrink-0",
                  activeTab === 'buy'
                    ? "font-semibold text-[#2d2923] dark:text-[#fdfdfd]"
                    : "font-medium text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]"
                )}>
                  Buy
                </p>
              </button>
              <button
                onClick={() => handleTabChange('sell')}
                className={cn(
                  "pb-2 pt-1 px-0 flex items-center justify-center shrink-0 transition-colors",
                  activeTab === 'sell'
                    ? "border-b border-[#2d2923] dark:border-[#fdfdfd] border-t-0 border-l-0 border-r-0 border-solid"
                    : ""
                )}
              >
                <p className={cn(
                  "font-plus-jakarta leading-[20px] text-[14px] shrink-0",
                  activeTab === 'sell'
                    ? "font-semibold text-[#2d2923] dark:text-[#fdfdfd]"
                    : "font-medium text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]"
                )}>
                  Sell
                </p>
              </button>
              <button
                onClick={handlePlaceOrder}
                className="pb-2 pt-1 px-0 flex items-center justify-center shrink-0 transition-colors"
              >
                <p className="font-plus-jakarta font-medium leading-[20px] text-[14px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] shrink-0">
                  Place new order
                </p>
              </button>
            </div>
          </div>
          {showViewSecondariesMarket && (
            <button
              className="relative shrink-0 w-[54px] h-[54px] rounded-full bg-white dark:bg-[#1c1c1c] hover:bg-gray-100 dark:hover:bg-[#121212] flex items-center justify-center transition-colors border border-[rgba(45,41,35,0.12)] dark:border-[rgba(253,253,253,0.12)]"
              onClick={() => (canReadFinancials ? navigate('/trading/otc') : enterPhaseTwo())}
              aria-label="View Secondaries Market"
            >
              <ArrowUpRight className="w-5 h-5 text-[#2d2923] dark:text-[#fdfdfd]" />
            </button>
          )}
        </div>
      )}

      {/* Filters and Table */}
      <div className="bg-[rgba(255,255,255,0.32)] dark:bg-[#0a0a0a] border border-white dark:border-[#171717] rounded-none p-2 flex flex-col">
        {!isMobile && (
          <div className="mb-3 w-full">
            <OTCFilters
              search={filters.search}
              onSearchChange={setters.setSearch}
              type={filters.type}
              onTypeChange={setters.setType}
              assetType={filters.assetType}
              onAssetTypeChange={setters.setAssetType}
              trancheSize={filters.trancheSize}
              onTrancheSizeChange={setters.setTrancheSize}
              sortBy={filters.sortBy}
              onSortByChange={setters.setSortBy}
              sortDirection={filters.sortDirection}
              onSortDirectionChange={setters.setSortDirection}
              financialsHidden={!canReadFinancials}
              className="w-full"
            />
          </div>
        )}
        <OrderbookTableContent
          data={data}
          isLoading={loading}
          onRowClick={handleOrderClick}
          sortColumn={filters.sortBy ? filters.sortBy.toLowerCase() : undefined}
          sortDirection={filters.sortDirection?.toLowerCase() as 'asc' | 'desc' | undefined}
          onSort={(columnKey) => {
            // Map column keys to SortBy values
            const sortByMap: Record<string, SortBy> = {
              'project_name': 'PROJECT_NAME',
              'offered_amount': 'OFFERED_AMOUNT',
              'date': 'DATE'
            }
            const newSortBy = sortByMap[columnKey] || 'DATE'
            
            // Toggle sort direction if same column, otherwise default to ASC
            const newSortDirection = filters.sortBy === newSortBy && filters.sortDirection === 'ASC' 
              ? 'DESC' 
              : 'ASC'
            
            setters.setSortBy(newSortBy)
            setters.setSortDirection(newSortDirection)
          }}
          // Mobile filters props
          search={filters.search}
          onSearchChange={setters.setSearch}
          type={filters.type}
          onTypeChange={setters.setType}
          assetType={filters.assetType}
          onAssetTypeChange={setters.setAssetType}
          trancheSize={filters.trancheSize}
          onTrancheSizeChange={setters.setTrancheSize}
          sortBy={filters.sortBy}
          onSortByChange={setters.setSortBy}
          sortDirectionFilter={filters.sortDirection}
          onSortDirectionChange={setters.setSortDirection}
          financialsHidden={!canReadFinancials}
        />
      </div>
      {showPagination && totalPages > 1 && (
        <div className="flex justify-center">
          <PaginationWrapper
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={goToPage}
          />
        </div>
      )}
      {showSummary && (
        <div className="text-center text-sm text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
          Showing {data.length} of {total} orders
        </div>
      )}

      {/* Order Inquiry Modal */}
      {showOrderInquiry && (
        <OrderInquiryModal
          isOpen={orderInquiryModal}
          onClose={() => {
            setOrderInquiryModal(false)
            setSelectedOrder(null)
          }}
          order={selectedOrder}
          onInquirySubmitted={handleInquirySubmitted}
        />
      )}
    </div>
  )
}

export default OrderbookTable 
